/** How a typed query narrows the option list. */
export type SelectFilterMode = 'startsWith' | 'contains';

/**
 * The form a label and a query are compared in: lower case, accents folded
 * (`Ü` → `u`, `é` → `e`), so „uberweisung“ finds „Überweisung“ and a typed
 * accent is no condition either.
 */
export function foldSearchText(text: string): string {
  return text
    .normalize('NFD')
    .replace(/\p{M}+/gu, '')
    .toLowerCase();
}

/** Whether `needle` starts a word inside `label` (after a non-letter, non-digit). */
function startsWordInside(label: string, needle: string): boolean {
  let from = label.indexOf(needle, 1);
  while (from > 0) {
    if (!/[\p{L}\p{N}]/u.test(label[from - 1]!)) return true;
    from = label.indexOf(needle, from + 1);
  }
  return false;
}

/**
 * Resolve which options survive a typed query, as indices into `labels`.
 *
 * Substring matching by default: option labels here are whole words a user
 * recognises rather than types from the front ("Rechnungsanschrift" is found by
 * "anschrift" just as legitimately as by "Rech"), and a prefix-only filter turns
 * a mistyped first letter into an empty list. `startsWith` narrows it back down.
 *
 * Prefix matches come first, then matches at the start of a later word
 * („pau“ → „Miete pauschal“), then the other substring matches — the obvious
 * hit stays at the top, as in `NxAutocompleteInput`'s `additionalMatches` pass.
 *
 * Matching ignores case and accents (`foldSearchText`) and leading whitespace on
 * a label, which mirrors `resolveTypeaheadIndex` next door: an indented label
 * is a rendering detail, not part of the word the user types.
 *
 * An empty (or whitespace-only) query is not a filter — every index survives.
 */
export function filterOptionIndices(
  labels: readonly string[],
  query: string,
  mode: SelectFilterMode = 'contains'
): number[] {
  const needle = foldSearchText(query.trim());
  if (needle.length === 0) return labels.map((_, index) => index);

  const prefixMatches: number[] = [];
  const wordMatches: number[] = [];
  const substringMatches: number[] = [];

  for (let index = 0; index < labels.length; index++) {
    const label = foldSearchText(labels[index].trimStart());
    if (label.startsWith(needle)) {
      prefixMatches.push(index);
    } else if (mode === 'contains' && label.includes(needle)) {
      if (startsWordInside(label, needle)) wordMatches.push(index);
      else substringMatches.push(index);
    }
  }

  return [...prefixMatches, ...wordMatches, ...substringMatches];
}
